import axios, { AxiosError, AxiosRequestConfig } from "axios"
import { onHttpRequestError } from "./onHttpRequestError"

axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL

export const getCustomerToken = (): string | null => {
  try {
    const token = localStorage.getItem("caocaoAccessToken")
    return token ? `Bearer ${token}` : null
  } catch {
    return null
  }
}

const withCustomerAuth = (headers: AxiosRequestConfig["headers"] = {}) => {
  const token = getCustomerToken()
  return token ? { ...headers, Authorization: token } : headers
}

const catchTransactionError = async <T>(asyncFunc: Promise<any>, apiName = ""): Promise<T | null> => {
  try {
    const response = await asyncFunc
    return response?.data ?? null
  } catch (error) {
    const err = error as AxiosError
    const response = {
      data: { data: null, error: null },
      message: err.message,
      ...(err.response ?? {}),
    }
    onHttpRequestError(response, apiName)
    return null
  }
}

interface ConfigsProps extends AxiosRequestConfig {
  apiName?: string
}

export const getRequest = async <T = any>(url: string, configs: ConfigsProps = {}): Promise<T | null> =>
  catchTransactionError<T>(axios.get(url, { ...configs, headers: withCustomerAuth(configs.headers) }), configs.apiName)

export const postRequest = async <T = any>(url: string, data?: any, configs: ConfigsProps = {}): Promise<T | null> =>
  catchTransactionError<T>(
    axios.post(url, data, { ...configs, headers: withCustomerAuth(configs.headers) }),
    configs.apiName,
  )

export const putRequest = async <T = any>(url: string, data?: any, configs: ConfigsProps = {}): Promise<T | null> =>
  catchTransactionError<T>(
    axios.put(url, data, { ...configs, headers: withCustomerAuth(configs.headers) }),
    configs.apiName,
  )

export const patchRequest = async <T = any>(url: string, data?: any, configs: ConfigsProps = {}): Promise<T | null> =>
  catchTransactionError<T>(
    axios.patch(url, data, { ...configs, headers: withCustomerAuth(configs.headers) }),
    configs.apiName,
  )

export const deleteRequest = async <T = any>(url: string, configs: ConfigsProps = {}): Promise<T | null> =>
  catchTransactionError<T>(
    axios.delete(url, {
      ...configs,
      headers: withCustomerAuth(configs.headers),
    }),
    configs.apiName,
  )

export class HttpClient {
  static get = getRequest
  static post = postRequest
  static put = putRequest
  static patch = patchRequest
  static delete = deleteRequest
}
